import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2, Upload, X, Download, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TEMPLATE_ICONS = { pptx: 'PPT', docx: 'DOC', pdf: 'PDF' };

/**
 * Inline Organization form — used inside the Admin → Organizations
 * dialog for both create + edit. Extracted out of AdminPage.js as part
 * of the Feb-2026 maintainability refactor so the parent stays focused
 * on list/tab orchestration rather than form internals.
 */
export default function OrgFormInline({ org, lookups, onSave, onCancel, saving }) {
  const [name, setName] = useState(org?.name || '');
  const [contactName, setContactName] = useState(org?.contact_name || '');
  const [contactEmail, setContactEmail] = useState(org?.contact_email || '');
  const [contactPhone, setContactPhone] = useState(org?.contact_phone || '');
  const [industry, setIndustry] = useState(org?.industry || '');
  const [country, setCountry] = useState(org?.country || '');
  const [lmsTypes, setLmsTypes] = useState(org?.lms_types ? org.lms_types.split(',').map(s => s.trim()).filter(Boolean) : []);
  const [audienceSizes, setAudienceSizes] = useState(org?.audience_sizes || '');
  const [primaryColor, setPrimaryColor] = useState(org?.primary_color || '#0F2040');
  const [secondaryColor, setSecondaryColor] = useState(org?.secondary_color || '#14B8A6');
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [templates, setTemplates] = useState(org?.templates || []);
  const [uploadingTemplate, setUploadingTemplate] = useState(false);

  useEffect(() => {
    if (logoFile) {
      const url = URL.createObjectURL(logoFile);
      setLogoPreview(url);
      return () => URL.revokeObjectURL(url);
    } else if (org?.logo) {
      setLogoPreview(`${API_URL}/api/organizations/${org.org_id}/logo`);
    }
  }, [logoFile, org]);

  const toggleLmsType = (type) => {
    setLmsTypes(prev => prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append('name', name);
    formData.append('contact_name', contactName);
    formData.append('contact_email', contactEmail);
    formData.append('contact_phone', contactPhone);
    formData.append('industry', industry);
    formData.append('country', country);
    formData.append('lms_types', lmsTypes.join(','));
    formData.append('audience_sizes', audienceSizes);
    formData.append('primary_color', primaryColor);
    formData.append('secondary_color', secondaryColor);
    if (logoFile) formData.append('logo', logoFile);
    onSave(formData);
  };

  const handleTemplateUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !org?.org_id) return;
    const ext = file.name.split('.').pop().toLowerCase();
    const typeMap = { pptx: 'pptx', docx: 'docx', pdf: 'pdf' };
    const templateType = typeMap[ext] || ext;

    setUploadingTemplate(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('type', templateType);
      const res = await axios.post(`${API_URL}/api/organizations/${org.org_id}/templates`, fd, { withCredentials: true });
      setTemplates(prev => [...prev, res.data]);
      toast.success(`Template "${file.name}" uploaded`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload template');
    } finally {
      setUploadingTemplate(false);
      e.target.value = '';
    }
  };

  const deleteTemplate = async (templateId, filename) => {
    if (!window.confirm(`Remove template "${filename}"?`)) return;
    try {
      await axios.delete(`${API_URL}/api/organizations/${org.org_id}/templates/${templateId}`, { withCredentials: true });
      setTemplates(prev => prev.filter(t => t.template_id !== templateId));
      toast.success('Template removed');
    } catch { toast.error('Failed to remove template'); }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Organization Name *</Label>
          <Input value={name} onChange={e => setName(e.target.value)} required className="text-sm mt-1" data-testid="org-name-input" />
        </div>
        <div>
          <Label className="text-xs">Industry</Label>
          <Select value={industry} onValueChange={setIndustry}>
            <SelectTrigger className="mt-1 text-sm" data-testid="org-industry-select"><SelectValue placeholder="Select industry" /></SelectTrigger>
            <SelectContent>
              {lookups.industries?.map(ind => <SelectItem key={ind} value={ind}>{ind}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Country</Label>
          <Select value={country} onValueChange={setCountry}>
            <SelectTrigger className="mt-1 text-sm" data-testid="org-country-select"><SelectValue placeholder="Select country" /></SelectTrigger>
            <SelectContent>
              {lookups.countries?.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Contact Name</Label>
          <Input value={contactName} onChange={e => setContactName(e.target.value)} className="text-sm mt-1" />
        </div>
        <div>
          <Label className="text-xs">Contact Email</Label>
          <Input type="email" value={contactEmail} onChange={e => setContactEmail(e.target.value)} className="text-sm mt-1" />
        </div>
        <div>
          <Label className="text-xs">Contact Phone</Label>
          <Input value={contactPhone} onChange={e => setContactPhone(e.target.value)} className="text-sm mt-1" />
        </div>
      </div>

      <div>
        <Label className="text-xs mb-1.5 block">LMS Type (optional)</Label>
        <div className="flex flex-wrap gap-1.5">
          {lookups.lms_types?.map(type => (
            <button key={type} type="button" onClick={() => toggleLmsType(type)}
              className={`px-2 py-1 rounded-md text-xs border transition-colors ${lmsTypes.includes(type) ? 'bg-[#0F2040] text-white border-[#0F2040]' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}
              data-testid={`lms-type-${type.replace(/\s+/g, '-').toLowerCase()}`}
            >{type}</button>
          ))}
        </div>
      </div>

      <div>
        <Label className="text-xs">Audience Size (optional)</Label>
        <Input value={audienceSizes} onChange={e => setAudienceSizes(e.target.value)} placeholder="e.g. 5000 users" className="text-sm mt-1" />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Primary Color</Label>
          <div className="flex items-center gap-2 mt-1">
            <input type="color" value={primaryColor} onChange={e => setPrimaryColor(e.target.value)} className="w-8 h-8 rounded border cursor-pointer" />
            <Input value={primaryColor} onChange={e => setPrimaryColor(e.target.value)} className="font-mono text-xs" />
          </div>
        </div>
        <div>
          <Label className="text-xs">Secondary Color</Label>
          <div className="flex items-center gap-2 mt-1">
            <input type="color" value={secondaryColor} onChange={e => setSecondaryColor(e.target.value)} className="w-8 h-8 rounded border cursor-pointer" data-testid="org-secondary-color" />
            <Input value={secondaryColor} onChange={e => setSecondaryColor(e.target.value)} className="font-mono text-xs" />
          </div>
        </div>
      </div>

      <div>
        <Label className="text-xs mb-1.5 block">Logo</Label>
        <div className="flex items-center gap-3">
          {logoPreview && (
            <img src={logoPreview} alt="Logo" className="w-12 h-12 rounded-lg object-contain border border-slate-200 bg-white p-1" onError={(e) => { e.target.style.display = 'none'; }} />
          )}
          <label className="cursor-pointer flex-1">
            <div className="flex items-center gap-2 px-3 py-2 rounded-md border border-dashed border-slate-300 hover:border-slate-400 text-xs text-slate-500">
              <Upload className="w-3.5 h-3.5" /> {logoFile ? logoFile.name : (org?.logo ? 'Replace logo' : 'Upload logo')}
            </div>
            <input type="file" accept="image/*" onChange={e => setLogoFile(e.target.files[0])} className="hidden" />
          </label>
          {(logoFile || org?.logo) && (
            <button type="button" onClick={() => { setLogoFile(null); setLogoPreview(null); }} className="text-slate-400 hover:text-red-500"><X className="w-3.5 h-3.5" /></button>
          )}
        </div>
      </div>

      {org?.org_id && (
        <div>
          <Label className="text-xs mb-1.5 block">Output Templates (PPT, Word, PDF)</Label>
          {templates.length > 0 && (
            <div className="space-y-1.5 mb-2">
              {templates.map(t => (
                <div key={t.template_id} className="flex items-center justify-between px-3 py-2 rounded-lg border border-slate-200 bg-slate-50 text-xs">
                  <div className="flex items-center gap-2 min-w-0">
                    <Badge variant="outline" className="text-[10px] font-mono shrink-0">{TEMPLATE_ICONS[t.type] || t.type?.toUpperCase()}</Badge>
                    <span className="truncate text-slate-700" title={t.original_filename}>{t.original_filename}</span>
                  </div>
                  <div className="flex items-center gap-1 shrink-0 ml-2">
                    <a href={`${API_URL}/api/organizations/${org.org_id}/templates/${t.template_id}/download`} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-[#0F2040] p-1" data-testid={`download-template-${t.template_id}`}>
                      <Download className="w-3.5 h-3.5" />
                    </a>
                    <button type="button" onClick={() => deleteTemplate(t.template_id, t.original_filename)} className="text-slate-400 hover:text-red-500 p-1" data-testid={`delete-template-${t.template_id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
          <label className="cursor-pointer block">
            <div className={`flex items-center justify-center gap-2 px-3 py-2 rounded-md border border-dashed border-slate-300 hover:border-slate-400 text-xs text-slate-500 ${uploadingTemplate ? 'opacity-50 pointer-events-none' : ''}`}>
              {uploadingTemplate ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
              {uploadingTemplate ? 'Uploading...' : 'Upload template (.pptx, .docx, .pdf)'}
            </div>
            <input type="file" accept=".pptx,.docx,.pdf" onChange={handleTemplateUpload} className="hidden" disabled={uploadingTemplate} />
          </label>
        </div>
      )}

      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" size="sm" onClick={onCancel}>Cancel</Button>
        <Button type="submit" size="sm" disabled={saving || !name.trim()} className="bg-[#0F2040]" data-testid="org-form-save">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}
          {org ? 'Update' : 'Create'}
        </Button>
      </div>
    </form>
  );
}
